import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, LayoutGrid, ShieldCheck, User, LogOut, Copy, Gauge, Users } from "lucide-react";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import api, { API } from "@/lib/api";
import SEO from "@/components/SEO";
import ProfileTab from "@/pages/collector/ProfileTab";
import SecurityTab from "@/pages/collector/SecurityTab";
import CertificatesTab from "@/pages/collector/CertificatesTab";
import MatchingTab from "@/pages/collector/MatchingTab";

const TABS = [
  { key: "overview",     label: "Overview",     Icon: LayoutGrid },
  { key: "certificates", label: "Certificates", Icon: KeyRound },
  { key: "profile",      label: "Profile",      Icon: User },
  { key: "matching",     label: "Matching",     Icon: Users },
  { key: "security",     label: "Security",     Icon: ShieldCheck },
];

export default function Dashboard() {
  const { collector, logout } = useCollectorAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const activeKey = params.get("tab") && TABS.some((t) => t.key === params.get("tab"))
    ? params.get("tab")
    : "overview";

  const setActive = (k) => {
    const p = new URLSearchParams(params);
    if (k === "overview") p.delete("tab"); else p.set("tab", k);
    setParams(p, { replace: true });
  };

  const signOut = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/login", { replace: true });
  };

  if (!collector) return null;
  const verified = collector.account_status === "active" && collector.email_verified_at;
  const avatarUrl = collector.avatar_media_id
    ? `${API}/me/profile/avatar?v=${collector.avatar_updated_at || collector.avatar_version || ""}`
    : null;

  return (
    <div className="min-h-[calc(100vh-140px)] bg-[var(--cream)]" data-testid="my-account-page">
      <SEO title="My Account · Collector Registry" />

      {/* -------- Header hero -------- */}
      <div className="border-b-2 border-[var(--ink)] bg-white">
        <div className="container-gm py-8">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] grid place-items-center shrink-0 shadow-[3px_3px_0_var(--ink)]">
              {avatarUrl
                ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-testid="my-account-avatar" />
                : <span className="font-display text-2xl text-[var(--ink)]" data-testid="my-account-avatar-initials">
                    {(collector.display_name || "?").trim().charAt(0).toUpperCase()}
                  </span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="kicker kicker-red">My Account</div>
              <h1 className="h-display text-3xl md:text-4xl mt-1 truncate"
                   data-testid="my-account-welcome">
                {collector.display_name || "Welcome back."}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-[var(--smoke)]">
                {collector.username && <span data-testid="my-account-username">@{collector.username}</span>}
                {collector.public_collector_id && (
                  <PublicIdChip id={collector.public_collector_id} />
                )}
                <StatusPill status={collector.account_status} />
              </div>
            </div>
            <button
              onClick={signOut}
              data-testid="my-account-signout"
              className="btn-ghost text-sm self-start md:self-auto"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="container-gm">
          <nav className="flex gap-1 overflow-x-auto" data-testid="my-account-tabs" role="tablist">
            {TABS.map(({ key, label, Icon }) => {
              const active = activeKey === key;
              return (
                <button
                  key={key}
                  onClick={() => setActive(key)}
                  data-testid={`my-account-tab-${key}`}
                  role="tab"
                  aria-selected={active}
                  className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-4 transition ${
                    active
                      ? "border-[var(--panini-red)] text-[var(--ink)]"
                      : "border-transparent text-[var(--smoke)] hover:text-[var(--ink)] hover:border-[var(--panini-yellow)]"
                  }`}
                >
                  <Icon size={16} /> {label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* -------- Tab body -------- */}
      <div className="container-gm py-10 max-w-5xl">
        {activeKey === "overview" &&      <Overview collector={collector} verified={verified} />}
        {activeKey === "certificates" &&  <CertificatesTab />}
        {activeKey === "profile" &&       <ProfileTab />}
        {activeKey === "matching" &&      <MatchingTab />}
        {activeKey === "security" &&      <SecurityTab />}
      </div>
    </div>
  );
}

function Overview({ collector, verified }) {
  const [score, setScore] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/me/score")
      .then((r) => { if (alive && r.data.ok) setScore(r.data.score); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <div className="space-y-8" data-testid="overview-tab">
      {!verified && (
        <div className="rounded-2xl border-2 border-[var(--panini-red)] bg-[var(--panini-red-soft)] p-5 flex items-start gap-3"
              data-testid="overview-verify-banner">
          <CheckCircle2 className="text-[var(--panini-red)] shrink-0 mt-0.5" size={22} />
          <div>
            <div className="font-bold text-[var(--ink)]">Verify your email to unlock claims.</div>
            <div className="text-sm text-[var(--smoke)] mt-1">
              Look for the verification link we sent to{" "}
              <span className="font-bold text-[var(--ink)]">{collector.email}</span>.
              Didn&apos;t get it? Head to the sign-in page and hit &quot;Resend&quot;.
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <CtaCard
          title="Claim a certificate"
          copy="Attach a certificate using its one-time claim code, or upload a legacy photo for review."
          Icon={KeyRound}
          to="/claim"
          badge={verified ? null : "Verify email first"}
          disabled={!verified}
          testid="overview-claim-cta"
        />
        <CtaCard
          title="Polish your profile"
          copy="Add a photo, bio, and social links so trades and shoutouts feel personal."
          Icon={User}
          to="/dashboard?tab=profile"
          badge={null}
          testid="overview-profile-cta"
          variant="yellow"
        />
      </div>

      <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[6px_6px_0_var(--ink)]"
            data-testid="overview-summary-card">
        <div className="kicker kicker-black">Account summary</div>
        <div className="mt-4 grid sm:grid-cols-2 gap-y-4 gap-x-8">
          <Row label="Display name" value={collector.display_name} testid="ov-display" />
          <Row label="Username" value={collector.username ? `@${collector.username}` : "—"} testid="ov-username" />
          <Row label="Email" value={collector.email} testid="ov-email" />
          <Row label="Public Collector ID"
                value={collector.public_collector_id || "Not yet issued"}
                mono={!!collector.public_collector_id} testid="ov-public-id" />
        </div>
      </div>

      {score && (
        <div className="rounded-3xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-6 md:p-8 shadow-[6px_6px_0_var(--ink)]"
              data-testid="overview-score-card">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div className="kicker kicker-black inline-flex items-center gap-2">
              <Gauge size={14} /> Collection score
            </div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
              Activity signal · not a ranking
            </div>
          </div>
          <div className="mt-3 flex items-end gap-3">
            <div className="font-display text-6xl text-[var(--panini-red)] leading-none" data-testid="ov-score-total">
              {score.total}
            </div>
            <div className="text-sm text-[var(--smoke)] pb-2">points</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-5">
            {Object.entries(score.breakdown).map(([k, v]) => (
              <div key={k} data-testid={`ov-score-${k}`}
                    className="rounded-xl border-2 border-[var(--ink)] bg-white p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--smoke)]">
                  {({
                    certificates: "Certificates",
                    claims_approved: "Claims",
                    achievements: "Achievements",
                    loyalty_years: "Loyalty",
                    verified_bonus: "Verified",
                  }[k]) || k}
                </div>
                <div className="text-lg font-display text-[var(--ink)]">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CtaCard({ title, copy, Icon, to, badge, disabled, testid, variant = "black" }) {
  const cls = variant === "yellow"
    ? "bg-[var(--panini-yellow)] text-[var(--ink)] shadow-[6px_6px_0_var(--ink)]"
    : "bg-[var(--ink)] text-white shadow-[6px_6px_0_var(--panini-yellow)]";
  return (
    <Link
      to={disabled ? "#" : to}
      onClick={(e) => disabled && e.preventDefault()}
      data-testid={testid}
      className={`group relative block rounded-3xl border-2 border-[var(--ink)] p-6 transition ${cls} ${
        disabled ? "opacity-60 cursor-not-allowed" : "hover:-translate-x-0.5 hover:-translate-y-0.5"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-white/15 border border-white/20 grid place-items-center shrink-0">
          <Icon size={20} className={variant === "yellow" ? "text-[var(--ink)]" : "text-[var(--panini-yellow)]"} />
        </div>
        <div className="flex-1">
          <div className="font-display text-xl">{title}</div>
          <div className={`text-sm mt-1 ${variant === "yellow" ? "text-[var(--ink)]/80" : "text-white/80"}`}>
            {copy}
          </div>
          {badge && (
            <span className="inline-block mt-3 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-white/15">
              {badge}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function PublicIdChip({ id }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(id); toast.success("Public Collector ID copied."); }
    catch { toast.error("Couldn't copy."); }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid="my-account-public-id"
      className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--panini-yellow-soft)] font-mono text-xs font-bold text-[var(--ink)] hover:bg-[var(--panini-yellow)]"
      title="Copy your Public Collector ID"
    >
      {id} <Copy size={11} />
    </button>
  );
}

function Row({ label, value, mono = false, testid }) {
  return (
    <div>
      <div className="text-[11px] font-bold tracking-widest text-[var(--smoke)] uppercase">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono text-[var(--panini-red)] font-bold" : "text-[var(--ink)] font-bold"}`}
            data-testid={testid}>
        {value || "—"}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: { bg: "var(--panini-yellow)", fg: "var(--ink)", label: "Active" },
    email_unverified: { bg: "#fef3c7", fg: "#92400e", label: "Verification pending" },
    suspended: { bg: "#fee2e2", fg: "#991b1b", label: "Suspended" },
    deactivated: { bg: "#e5e7eb", fg: "#374151", label: "Deactivated" },
  };
  const cfg = map[status] || { bg: "#e5e7eb", fg: "#374151", label: status || "Unknown" };
  return (
    <span
      className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-widest"
      style={{ background: cfg.bg, color: cfg.fg }}
      data-testid="my-account-status-pill"
    >
      {cfg.label}
    </span>
  );
}
